/**
 * Underwriter "ignore" for auto claims and violations (#36871), shared by the policy Incidents tab and
 * the offer's factors modal.
 *
 * Each driver keeps an `ignoredIncidents` list of { date, class }. The rater matches it across every
 * driver and flags each ignored `autoViolationDates` entry `ignoredByUnderwriting`, so the row status
 * here reads that server flag rather than re-deriving it. Only the most severe incident on a date
 * counts, so the status of the others is worked out the way the rater picks (getViolationForDate in
 * @ourbranch/underwriting-rules).
 */
import { addMonths, isAfter, isValid, parseISO, subMonths } from 'date-fns';
import { cfmAutoViolationSeverity, homeClassForAutoClaimClass, ratingLookbackByState } from '@ourbranch/lookups';

export const IncidentStatus = {
  Counts: 'counts',
  DoesNotCount: 'doesNotCount',
  Ignored: 'ignored',
  IgnoredByPriorCarrier: 'ignoredByPriorCarrier'
};

export const incidentStatusLabels = {
  [IncidentStatus.Counts]: 'Counts now',
  [IncidentStatus.DoesNotCount]: "Doesn't count now",
  [IncidentStatus.Ignored]: 'Ignored',
  [IncidentStatus.IgnoredByPriorCarrier]: 'Ignored by prior carrier'
};

const parseJson = (value, fallback) => {
  if (typeof value !== 'string') {
    return value ?? fallback;
  }
  try {
    return JSON.parse(value) ?? fallback;
  } catch {
    return fallback;
  }
};

const incidentKey = (date, incidentClass) => `${date}|${incidentClass}`;

// An incident the rater can count: not ignored by us or the prior carrier, and (on an imported CFM
// term) one the prior carrier was charging.
const isCountable = (incident) =>
  incident.ignoredByUnderwriting !== true &&
  incident.ignored !== true &&
  incident.ratedByGo !== false &&
  incident.chargeable !== false;

// Same order as getViolationForDate: claims first, then version, points, and class.
const pickCountingIncident = (incidents) => {
  const claims = incidents.filter((incident) => String(incident.source).toLowerCase() === 'claims');
  return [...(claims.length ? claims : incidents)].sort((a, b) => {
    if ((a.version || 1) !== (b.version || 1)) {
      return (b.version || 1) - (a.version || 1);
    }
    if (a.points !== b.points) {
      return (b.points || 0) - (a.points || 0);
    }
    return String(a.class).toUpperCase() < String(b.class).toUpperCase() ? -1 : 1;
  })[0];
};

// The rater's own age limits on the incident it picks for a date (auto post-data rules #setAutoViolations):
// a license suspension (FDL) counts for 12 months and a UDR for 35, from the term's effective date.
const AGES_OUT_AFTER_MONTHS = { FDL: 12, UDR: 35 };
const hasAgedOut = (incident, date, effectiveDate) => {
  const months = AGES_OUT_AFTER_MONTHS[incident.class];
  const effective = effectiveDate && parseISO(effectiveDate);
  return !!months && isValid(effective) && isAfter(effective, addMonths(parseISO(date), months));
};

// CFM's tier counts look back 36 months and Violation Free the state's rating lookback (59 by default);
// a tiered violation older than both doesn't count (CFM auto post-data rules #setAutoRatingFlags).
const CFM_TIER_WINDOW_MONTHS = 36;
const DEFAULT_RATING_LOOKBACK_MONTHS = 59;
const isInCfmRatingWindow = (date, effectiveDate, state) => {
  const effective = effectiveDate && parseISO(effectiveDate);
  if (!isValid(effective)) {
    return true;
  }
  const months = Math.max(CFM_TIER_WINDOW_MONTHS, ratingLookbackByState[state] ?? DEFAULT_RATING_LOOKBACK_MONTHS);
  return isAfter(parseISO(date), subMonths(effective, months));
};

const getStatus = (incident, countingIncident, countsEveryTieredViolation, inCfmRatingWindow = true) => {
  if (incident.ignoredByUnderwriting) {
    return IncidentStatus.Ignored;
  }
  if (incident.ignored) {
    return IncidentStatus.IgnoredByPriorCarrier;
  }
  // On CFM a tiered violation is rated only through the tier counts (major, mid-major, minor), which take
  // every one on a date inside the window, and Violation Free, which an imported row the prior carrier
  // wasn't charging can still deny; the date's pick decides everything else, claims included
  const deniesViolationFree = countsEveryTieredViolation && incident.deniesViolationFreeOnImport === true;
  if (countsEveryTieredViolation && cfmAutoViolationSeverity[incident.class]) {
    return (inCfmRatingWindow && isCountable(incident)) || deniesViolationFree
      ? IncidentStatus.Counts
      : IncidentStatus.DoesNotCount;
  }
  return incident === countingIncident || deniesViolationFree ? IncidentStatus.Counts : IncidentStatus.DoesNotCount;
};

/**
 * One row per auto claim or violation, every incident on a date included, in date order per driver.
 * `countsEveryTieredViolation` is the brand's countsEveryTieredAutoViolation flag (CFM); `effectiveDate` is
 * the term's auto effective date, which the rater ages incidents out from, and `state` sets CFM's lookback.
 */
export const buildAutoIncidentRows = (drivers, { countsEveryTieredViolation = false, effectiveDate, state } = {}) => {
  const stamps = new Map(
    (drivers || []).flatMap((driver) =>
      (driver?.ignoredIncidents || []).map((ignored) => [incidentKey(ignored.date, ignored.class), ignored])
    )
  );
  // the row's own driver's stamp first: CFM can hold the same key on two drivers for separate incidents
  const stampFor = (driver, date, incidentClass) =>
    (driver?.ignoredIncidents || []).find(
      (ignored) => incidentKey(ignored.date, ignored.class) === incidentKey(date, incidentClass)
    ) || stamps.get(incidentKey(date, incidentClass));
  return (drivers || []).flatMap((driver) =>
    Object.entries(parseJson(driver?.autoViolationDates, {}) || {}).flatMap(([date, incidents]) => {
      const onDate = (incidents || []).filter(Boolean);
      const countingIncident = pickCountingIncident(onDate.filter(isCountable));
      return onDate.map((incident) => {
        // the age limits apply to the date's pick; a CFM tier or Violation Free input still counts
        const inCfmRatingWindow = isInCfmRatingWindow(date, effectiveDate, state);
        const countsOnlyAsPick =
          getStatus(incident, null, countsEveryTieredViolation, inCfmRatingWindow) !== IncidentStatus.Counts;
        const pickedStatus = getStatus(incident, countingIncident, countsEveryTieredViolation, inCfmRatingWindow);
        const status =
          incident === countingIncident && countsOnlyAsPick && hasAgedOut(incident, date, effectiveDate)
            ? IncidentStatus.DoesNotCount
            : pickedStatus;
        const stamp = status === IncidentStatus.Ignored ? stampFor(driver, date, incident.class) : undefined;
        return {
          id: `${driver.id}|${date}|${incident.class}`,
          driverId: driver.id,
          driver: `${driver.firstName || ''} ${driver.lastName || ''}`.trim(),
          date,
          type: incident.class,
          source: incident.source,
          addedBy: incident.addedBy,
          isCourtRecord: !!incident.isCourtRecord,
          status,
          ignoredBy: stamp?.ignoredBy,
          ignoredAt: stamp?.ignoredAt,
          // UDRs are cleared with "Clear UDRs"; the prior carrier's decision is read-only
          canIgnore: incident.class !== 'UDR' && status !== IncidentStatus.IgnoredByPriorCarrier,
          // a CFM driver's own imported row matches only that driver's ignore list
          isDriversOwnRecord: incident.deniesViolationFreeOnImport !== undefined
        };
      });
    })
  );
};

const listsByDriver = (drivers) =>
  new Map(
    (drivers || []).map((driver) => [
      driver.id,
      (driver.ignoredIncidents || []).map(({ date, class: c }) => ({ date, class: c }))
    ])
  );

/**
 * The drivers whose list changes, as { [driverId]: list }, after ignoring these rows. Each key goes on the
 * row's own driver: a CFM driver's own imported row only matches that driver's list.
 */
export const getIgnoredIncidentsAfterIgnoring = (drivers, rows) => {
  const lists = listsByDriver(drivers);
  const changed = {};
  for (const row of rows) {
    const list = changed[row.driverId] || lists.get(row.driverId);
    const key = incidentKey(row.date, row.type);
    if (!list || list.some((ignored) => incidentKey(ignored.date, ignored.class) === key)) {
      continue;
    }
    changed[row.driverId] = [...list, { date: row.date, class: row.type }];
  }
  return changed;
};

/**
 * The drivers whose list changes, as { [driverId]: list }, after they stop ignoring these rows. Pass the
 * removed drivers too: their lists still apply to rows that moved onto the drivers who stay.
 */
export const getIgnoredIncidentsAfterStopping = (drivers, rows) => {
  const changed = {};
  for (const [driverId, list] of listsByDriver(drivers)) {
    const stopKeys = new Set(
      rows
        .filter((row) => !row.isDriversOwnRecord || row.driverId === driverId)
        .map((row) => incidentKey(row.date, row.type))
    );
    const kept = list.filter((ignored) => !stopKeys.has(incidentKey(ignored.date, ignored.class)));
    if (kept.length !== list.length) {
      changed[driverId] = kept;
    }
  }
  return changed;
};

/**
 * The driver's other incidents on the dates of the selected rows, which the dialog offers too: ones that
 * can still be ignored, or with `stopIgnoring`, ones that are ignored.
 */
export const getOtherIncidentsOnSameDates = (rows, selectedRows, { stopIgnoring = false } = {}) => {
  const selectedIds = new Set(selectedRows.map((row) => row.id));
  const driverDates = new Set(selectedRows.map((row) => `${row.driverId}|${row.date}`));
  return rows.filter(
    (row) =>
      !selectedIds.has(row.id) &&
      driverDates.has(`${row.driverId}|${row.date}`) &&
      (stopIgnoring ? row.status === IncidentStatus.Ignored : row.canIgnore && row.status !== IncidentStatus.Ignored)
  );
};

/**
 * The home copies (date + home class) of these auto rows that the home price still counts. A bundled
 * offer copies an applicant's auto claims onto the home under homeClassForAutoClaimClass.
 */
export const getHomeCopiesToIgnore = (quote, rows) => {
  const homeCopies = new Set(
    Object.values(parseJson(quote?.homeViolationPeople, {}) || {})
      .flat()
      .map((copy) => incidentKey(copy?.violationDate, copy?.homeClass))
  );
  const alreadyIgnored = new Set(
    (quote?.ignoredHomeIncidents || []).map((ignored) => incidentKey(ignored.date, ignored.class))
  );
  const copies = new Map();
  for (const row of rows) {
    const homeClass = homeClassForAutoClaimClass[row.type];
    const key = incidentKey(row.date, homeClass);
    if (homeClass && homeCopies.has(key) && !alreadyIgnored.has(key)) {
      copies.set(key, { date: row.date, class: homeClass });
    }
  }
  return [...copies.values()];
};
